import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Component, createRef, } from 'react';
import * as ProjectAPI from 'api/project-api';
import { getLanguageFromTrsx } from 'api/project-api';
import { subtitlesDefaults } from 'api/settings/user-settings';
import { Select, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { txt } from 'libs/i18n';
import { summarizeProject } from 'api/model/services/services-api';
import { formatTimeToISOString } from 'components/TimerangeSelectionForm';
import deepEqual from 'fast-deep-equal';
import clsx from 'clsx';
import { parseTrsx } from '@newtontechnologies/beey-trsx-js/';
import { BeeyLocale } from 'libs/locales/locale';
import SpeakerEditorModal from './SpeakerEditorModal';
import SummaryModal from './SummaryModal';
import ProjectAlert from './ProjectAlert';
import SectionTagsModal from './SectionTagsModal';
import { playbackContext } from '../contexts/playback';
import ContextMenu from '../ContextMenu';
import { setSavingStateSignal } from '../signalsStore/savingState';
import DocumentSaver from './document-saver';
import { Recognition } from './recognition';
import DocumentHoverElement from './DocumentHoverElement';
import SectionNameModal from './SectionNameModal';
import { QUILL_CURSOR } from './text-utils';
import DocumentWidgets from './DocumentWidgets';
import MicTranscriptionDots from './MicTranscriptionDots';
import styles from './style.module.less';
import { displayNotification } from './project-save-notification';
import { computeDocumentVerticalLayout } from '../EditorContent/editor-positions';
import { projectStateSignal } from '../contexts/projectSignal';
import { transcriptionSourceSignal } from '../signalsStore/transcriptionSource';
import 'quill/dist/quill.snow.css';
const DEFAULT_TRANSCRIPTION_LANGUAGE_CODE = 'en-US';
export default class DocumentEditor extends Component {
    constructor(props, context) {
        super(props, context);
        this.connectedMessageStream = null;
        this.pluginsTimerId = undefined;
        this.isStreamingSessionActive = false;
        this.reloadTrsxWithLabel = async (label) => {
            const { editorController, isSecondary, project, lock, session, onErrorStatusChange, } = this.props;
            let trsxResult = await this.loadTrsx('currentTrsx', undefined, label);
            let sourceVariant = 'currentTrsx';
            const notFoundForLabel = trsxResult.isFail() && trsxResult.err() === 'not-found' && label !== undefined;
            if (notFoundForLabel) {
                this.logger.info('currentTrsx not found for label "{label}", trying initialTrsx.', { label });
                trsxResult = await this.loadTrsx('initialTrsx', undefined, label);
                sourceVariant = 'initialTrsx';
            }
            const initialAlsoNotFound = trsxResult.isFail() && trsxResult.err() === 'not-found' && label !== undefined;
            const canAccessFailedTranscriptions = session.login.hasClaim('projects:accessFailedTranscriptions');
            if (initialAlsoNotFound) {
                this.logger.info('initialTrsx not found for label "{label}", trying partialTrsx.', { label });
                trsxResult = await this.loadTrsx('partialTrsx', undefined, label);
                sourceVariant = 'partialTrsx';
                if (trsxResult.isSuccess() && !canAccessFailedTranscriptions) {
                    this.logger.info('PartialTrsx found for label "{label}" but user lacks claim for partial access.', { label });
                    this.updateDocumentLoadingState('no-document');
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange({ category: 'Client', reason: 'FailedTranscriptionAccessDenied' });
                    return;
                }
            }
            if (trsxResult.isSuccess()) {
                const trsx = trsxResult.get();
                if (!isSecondary && this.documentSaver !== null) {
                    this.documentSaver.handleSaveToPartial(sourceVariant === 'partialTrsx');
                }
                const isReadOnly = isSecondary || project.isReadOnly || lock.isLocked;
                if (isReadOnly) {
                    editorController.enableEditing();
                }
                const trsxToImport = sourceVariant === 'initialTrsx' ? Object.assign(Object.assign({}, trsx), { etag: null }) : trsx;
                this.importTrsxToEditorController(trsxToImport, label !== null && label !== void 0 ? label : null, sourceVariant);
                editorController.initializeAligner(trsx.content);
                if (isReadOnly) {
                    editorController.disableEditing();
                }
                this.updateDocumentLoadingState('loaded');
                if (sourceVariant === 'partialTrsx') {
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange({ category: 'Client', reason: 'FailedTranscriptionPartialLoaded' });
                }
                else {
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange('no-error');
                }
                this.logger.info('Successfully reloaded TRSX with label "{label}" from {variant}.', {
                    label: label !== null && label !== void 0 ? label : 'none',
                    variant: sourceVariant,
                });
            }
            else {
                this.logger.error('Failed to reload TRSX with label "{label}": {error}', {
                    label: label !== null && label !== void 0 ? label : 'none',
                    error: trsxResult.err(),
                });
                this.updateDocumentLoadingState('no-document');
            }
        };
        this.loadTrsx = async (variant, fileId, label) => {
            const { session, project } = this.props;
            return ProjectAPI.fetchTrsx(session.connection, project.id, variant, fileId, label);
        };
        this.initializeEditor = async () => {
            this.logger.info('Starting TRSX loading logic.');
            const { messageStream, isSecondary, editorController, trsxLabel, isDoubleEditorMode, onErrorStatusChange, } = this.props;
            if (isSecondary) {
                const { trsxVariantToLoad, trsxFileId } = this.props;
                const preferredVariant = trsxVariantToLoad !== null && trsxVariantToLoad !== void 0 ? trsxVariantToLoad : 'currentTrsx';
                this.logger.info('Initializing as secondary editor. Trying variant "{variant}".', {
                    variant: preferredVariant,
                    label: trsxLabel,
                });
                let trsx = await this.loadTrsx(preferredVariant, trsxFileId, trsxLabel);
                if (trsx.isFail() && preferredVariant === 'currentTrsx' && trsxFileId !== undefined) {
                    this.logger.info('currentTrsx not found for this ID, trying initialTrsx fallback.');
                    trsx = await this.loadTrsx('initialTrsx', trsxFileId);
                }
                if (trsx.isSuccess()) {
                    const trsxToImport = trsx.get();
                    this.importTrsxToEditorController(trsxToImport, trsxLabel, preferredVariant);
                    return trsxToImport;
                }
                this.logger.warn('Could not load any TRSX variant for secondary editor.');
                return null;
            }
            if (this.isMicSource) {
                this.logger.info('Initializing for microphone source. Skipping TRSX load, connecting to message stream.');
                if (this.documentSaver !== null) {
                    this.documentSaver.handleSaveToPartial(true);
                }
                const { stream } = await messageStream.connect();
                this.connectedMessageStream = stream;
                return null;
            }
            const label = isDoubleEditorMode ? trsxLabel : undefined;
            let variant = 'currentTrsx';
            this.logger.info('Attempting to load TRSX variant "{variant}" with label "{label}".', {
                variant,
                label: label !== null && label !== void 0 ? label : 'None',
            });
            const currentTrsx = await this.loadTrsx('currentTrsx', undefined, label);
            if (currentTrsx.isSuccess()) {
                this.logger.info('Successfully loaded TRSX variant "{variant}".', { variant });
                if (this.documentSaver !== null) {
                    this.documentSaver.handleSaveToPartial(false);
                }
                const trsx = currentTrsx.get();
                this.importTrsxToEditorController(trsx, label !== null && label !== void 0 ? label : null, 'currentTrsx');
                onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange('no-error');
                const { stream } = await messageStream.connect(undefined, true);
                this.connectedMessageStream = stream;
                const { project } = this.props;
                if (project.processingState !== 'InProgress') {
                    editorController.lastHandledPhraseTime = Infinity;
                    if (editorController.lastHandledMessageId !== Infinity) {
                        this.logger.info('Transcription not in progress. Setting lastHandledMessageId to Infinity to prevent duplicate content from stale message stream. Was {previous}.', { previous: editorController.lastHandledMessageId });
                        editorController.lastHandledMessageId = Infinity;
                    }
                }
                return trsx;
            }
            if (currentTrsx.err() !== 'not-found') {
                this.logger.error('Failed to load TRSX variant "{variant}" with an unexpected error: {error}', { error: currentTrsx.err(), variant });
                throw new Error('Failed to load currentTrsx');
            }
            const { project, session } = this.props;
            variant = 'initialTrsx';
            this.logger.info('CurrentTrsx not found. Attempting to load TRSX variant "{variant}".', { variant });
            const initialTrsx = await this.loadTrsx('initialTrsx');
            if (initialTrsx.isSuccess()) {
                this.logger.info('Successfully loaded TRSX variant "{variant}".', { variant });
                if (this.documentSaver !== null) {
                    this.documentSaver.handleSaveToPartial(this.isMicSource);
                }
                const trsx = { content: initialTrsx.get().content, etag: null };
                this.importTrsxToEditorController(trsx, null, 'initialTrsx');
                onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange('no-error');
                const { stream } = await messageStream.connect(undefined, true);
                this.connectedMessageStream = stream;
                return trsx;
            }
            if (initialTrsx.err() !== 'not-found') {
                this.logger.error('Failed to load TRSX variant "{variant}" with an unexpected error: {error}', { error: initialTrsx.err(), variant });
                throw new Error('Failed to load initialTrsx');
            }
            variant = 'partialTrsx';
            this.logger.info('CurrentTrsx and initialTrsx not found. Attempting to load TRSX variant "{variant}".', { variant });
            const partialTrsx = await this.loadTrsx('partialTrsx');
            if (partialTrsx.isSuccess()) {
                if (project.processingState === 'Failed' && !session.login.hasClaim('projects:accessFailedTranscriptions')) {
                    this.logger.info('PartialTrsx found but user lacks claim to access partial transcriptions of failed projects.');
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange({ category: 'Client', reason: 'FailedTranscriptionAccessDenied' });
                    return null;
                }
                this.logger.info('Successfully loaded TRSX variant "{variant}".', { variant });
                const trsx = partialTrsx.get();
                if (this.documentSaver !== null) {
                    this.documentSaver.handleSaveToPartial(true);
                }
                const parsedTrsx = parseTrsx(trsx.content);
                if (!parsedTrsx.success) {
                    this.logger.error('Failed to parse {variant} content: {error}', { error: parsedTrsx.error, variant });
                    throw new Error(parsedTrsx.error);
                }
                this.importTrsxToEditorController(trsx, label !== null && label !== void 0 ? label : null, 'partialTrsx');
                const metadata = parsedTrsx.value.getGeneralMetadata();
                const lastMessageIdFromMetadata = metadata.lastMessageId !== undefined
                    ? metadata.lastMessageId.toString()
                    : undefined;
                this.logger.info('Connecting to message stream from last message ID: {lastMessageId}.', { lastMessageId: lastMessageIdFromMetadata !== null && lastMessageIdFromMetadata !== void 0 ? lastMessageIdFromMetadata : 'none' });
                if (lastMessageIdFromMetadata !== undefined) {
                    editorController.lastHandledMessageId = Number(lastMessageIdFromMetadata);
                }
                const { stream } = await messageStream.connect(lastMessageIdFromMetadata);
                this.connectedMessageStream = stream;
                editorController.fillPartialTrsx(trsx);
                editorController.updateEtag(trsx.etag);
                if (project.processingState === 'Failed') {
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange({ category: 'Client', reason: 'FailedTranscriptionPartialLoaded' });
                }
                return trsx;
            }
            if (partialTrsx.err() !== 'not-found') {
                this.logger.error('Failed to load TRSX variant "{variant}" with an unexpected error: {error}', { error: partialTrsx.err(), variant });
                throw new Error('Failed to load partialTrsx');
            }
            this.logger.info('No TRSX found (current, partial, or initial). Starting with a blank editor and connecting to message stream.');
            const { stream } = await messageStream.connect();
            this.connectedMessageStream = stream;
            if (this.documentSaver !== null) {
                this.documentSaver.handleSaveToPartial(true);
            }
            return null;
        };
        this.syncEditorLanguageFromTranscriptionConfig = async () => {
            var _a, _b, _c, _d;
            const { editorController, project, session, enqueueProjectUpdate, } = this.props;
            try {
                await enqueueProjectUpdate(async (currentProject) => {
                    const serverProject = await ProjectAPI.fetchProject(session.connection, currentProject.id);
                    return serverProject !== 'not-found' ? serverProject : currentProject;
                });
            }
            catch (error) {
                this.logger.warn('Could not refresh project before setting transcription language: {error}', {
                    error: String(error),
                });
            }
            const projectState = projectStateSignal.value;
            const updatedProject = projectState.phase === 'loaded' ? projectState.project : project;
            const code = (_c = (_b = (_a = updatedProject.transcriptionConfig) === null || _a === void 0 ? void 0 : _a.language) !== null && _b !== void 0 ? _b : session.login.user.settings.transcriptionSettings.recognitionLocale) !== null && _c !== void 0 ? _c : DEFAULT_TRANSCRIPTION_LANGUAGE_CODE;
            editorController.setLanguage((_d = BeeyLocale.fromCode(code)) !== null && _d !== void 0 ? _d : BeeyLocale.const(DEFAULT_TRANSCRIPTION_LANGUAGE_CODE));
        };
        this.handleTranscriptionStarted = async () => {
            const { onTranscriptionStarted } = this.props;
            this.isStreamingSessionActive = true;
            await this.syncEditorLanguageFromTranscriptionConfig();
            if (this.documentSaver !== null) {
                this.logger.info('Transcription started. Switching save target to partialTrsx.');
                this.documentSaver.handleSaveToPartial(true);
                this.documentSaver.handleTranscriptionStarted();
            }
            onTranscriptionStarted === null || onTranscriptionStarted === void 0 ? void 0 : onTranscriptionStarted();
        };
        this.handleTranscriptionComplete = async () => {
            const { editorController, lock, onTranscriptionCompleted } = this.props;
            this.isStreamingSessionActive = false;
            if (this.recognition !== null) {
                await this.recognition.handleTranscriptionComplete();
            }
            if (!lock.isLocked) {
                editorController.triggerSave();
            }
            onTranscriptionCompleted === null || onTranscriptionCompleted === void 0 ? void 0 : onTranscriptionCompleted();
        };
        this.checkTranscriptionAccess = async () => {
            const { session, onErrorStatusChange, isDoubleEditorMode, trsxLabel, } = this.props;
            const label = isDoubleEditorMode ? trsxLabel : undefined;
            const currentTrsx = await this.loadTrsx('currentTrsx', undefined, label);
            if (currentTrsx.isSuccess()) {
                onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange('no-error');
                return;
            }
            const initialTrsx = await this.loadTrsx('initialTrsx', undefined, label);
            if (initialTrsx.isSuccess()) {
                onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange('no-error');
                return;
            }
            const partialTrsx = await this.loadTrsx('partialTrsx', undefined, label);
            if (partialTrsx.isSuccess()) {
                if (!session.login.hasClaim('projects:accessFailedTranscriptions')) {
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange({ category: 'Client', reason: 'FailedTranscriptionAccessDenied' });
                }
                else {
                    onErrorStatusChange === null || onErrorStatusChange === void 0 ? void 0 : onErrorStatusChange({ category: 'Client', reason: 'FailedTranscriptionPartialLoaded' });
                }
            }
        };
        this.handleHistoryLoaded = () => {
            const { editorController, project } = this.props;
            void editorController.keywords.highlightAllKeywords(project.keywordsHighlight);
        };
        this.showSpeakerReplaceModal = (speakerNode, replacingEverywhere) => {
            const { editorController } = this.props;
            const speaker = editorController.speakers.getSpeakerByElement(speakerNode);
            if (speaker !== null) {
                this.setState({
                    speakerSelectDialog: {
                        visible: true,
                        node: speakerNode,
                        replacingEverywhere,
                        replacedSpeaker: speaker,
                    },
                });
            }
        };
        this.showAssignSpeakerModal = () => {
            this.setState({
                speakerSelectDialog: {
                    visible: true,
                    node: null,
                    replacingEverywhere: false,
                    replacedSpeaker: null,
                },
            });
        };
        this.showSectionNameModal = (target) => {
            this.setState({
                sectionName: {
                    modalVisible: true,
                    target: target !== null && target !== void 0 ? target : null,
                },
            });
        };
        this.showSummaryModal = (summaryIndex) => {
            this.setState({
                summary: {
                    modalVisible: true,
                    from: summaryIndex.from,
                    to: summaryIndex.to,
                    state: 'ready',
                },
            });
        };
        this.closeSummaryModal = () => {
            const { summary } = this.state;
            this.setState({ summary: Object.assign(Object.assign({}, summary), { modalVisible: false }) });
        };
        this.closeSectionNameModal = () => {
            const { sectionName } = this.state;
            this.setState({ sectionName: Object.assign(Object.assign({}, sectionName), { modalVisible: false }) });
        };
        // TODO: Refactoring: it would be nice to move most of the code in generateSummary to
        // SummaryModal to avoid clutter in DocumentEditor.
        this.generateSummary = async (formValues) => {
            const { summary } = this.state;
            const { session, project, editorController } = this.props;
            const beginInIsoFormat = formatTimeToISOString(editorController.textMetadata.getBeginAtIndex(summary.from));
            const endTime = editorController.textMetadata.getEndAtIndex(summary.to);
            const endInIsoFormat = formatTimeToISOString(endTime === Infinity ? 1000000 : endTime);
            this.setState({ summary: Object.assign(Object.assign({}, summary), { state: 'loading' }) });
            const result = await summarizeProject(session.connection, project.id, formValues.summaryLocale, beginInIsoFormat, endInIsoFormat);
            if (result.isSuccess()) {
                editorController.insertTextWithFormat(summary.from, result.get().text, { summary: 'button' });
                editorController.summary.deletePreviousSummary(summary.from);
                this.setState({ summary: Object.assign(Object.assign({}, summary), { modalVisible: false, state: 'ready' }) });
            }
            else {
                this.setState({
                    summary: Object.assign(Object.assign({}, summary), { modalVisible: true, state: 'failed', errorMessage: result.err() }),
                });
            }
        };
        this.showEditSectionTagsModal = (node) => {
            const { notification } = this.props;
            const getTagsFromNode = (element) => {
                // tag value must be saved as a class. If it were saved another
                // way, quill may not preserve it if the node is updated. Quill always preserves classes.
                const tags = [];
                const prefix = 'tagname-';
                for (let i = 0; i < element.classList.length; i += 1) {
                    const className = element.classList[i];
                    if (className.startsWith(prefix)) {
                        const decodedTag = decodeURIComponent(className.substring(prefix.length));
                        tags.push(decodedTag);
                    }
                }
                return tags;
            };
            if (node.textContent === '' || node.textContent === QUILL_CURSOR) {
                // NOTE: Adding tags to empty sections causes various problems and these tags cannot be
                // saved to trsx. We minimise these problems by not allowing adding tag to empty heading.
                notification.error({
                    message: txt('noTagForEmptyHeading'),
                    placement: 'topRight',
                    duration: 10,
                });
                return;
            }
            this.setState({
                sectionTagsModal: {
                    visible: true,
                    tags: getTagsFromNode(node),
                    node,
                },
            });
        };
        this.handleSpeakerSelected = (speaker, replacedSpeaker, replacingEverywhere, isCitation = false) => {
            const { editorController, tutorialCurrentStep, tutorialGoNext } = this.props;
            const { speakerSelectDialog } = this.state;
            editorController.handleSpeakerSelected(speaker, replacingEverywhere, replacedSpeaker, isCitation ? 'citation' : null);
            this.setState({ speakerSelectDialog: Object.assign(Object.assign({}, speakerSelectDialog), { replacingEverywhere }) });
            if (tutorialCurrentStep === 4) {
                tutorialGoNext();
            }
        };
        this.closeSectionTagsModal = () => {
            this.setState({
                sectionTagsModal: {
                    visible: false,
                    tags: [],
                    node: null,
                },
            });
        };
        this.handleSectionTagsSelected = (tags, targetNode) => {
            const { editorController } = this.props;
            editorController.handleSectionTagsSelected(tags, targetNode);
            this.closeSectionTagsModal();
        };
        this.closeSpeakerSelectDialog = () => {
            const { speakerSelectDialog } = this.state;
            const { editorController } = this.props;
            if (!speakerSelectDialog.visible)
                return;
            this.setState({
                speakerSelectDialog: {
                    visible: false, node: null, replacedSpeaker: null, replacingEverywhere: false,
                },
            });
            editorController.handleCloseSpeakerSelector();
        };
        this.handleWidgetsClicked = (node, action) => {
            if (action === 'editSections') {
                this.showEditSectionTagsModal(node);
            }
        };
        this.checkPlugins = () => {
            const { notification } = this.props;
            const grammarlyPopups = document.querySelector('grammarly-popups');
            if (grammarlyPopups !== null) {
                notification.warning({
                    message: txt('activeGrammarlyTitle'),
                    description: txt('activePluginDescription'),
                    duration: 0,
                });
            }
            const deeplController = document.querySelector('deepl-input-controller');
            if (deeplController !== null) {
                notification.warning({
                    message: txt('activeDeeplTitle'),
                    description: txt('activePluginDescription'),
                    duration: 0,
                });
            }
        };
        this.checkRecentEdits = () => {
            const { editorController, notification } = this.props;
            const warning = editorController.getRecentEditWarning();
            if (warning === null)
                return;
            notification.warning({
                message: txt('someoneIsEditingHead'),
                description: `${txt('someoneIsEditingDescription')} ${warning}`,
                placement: 'topRight',
                duration: 0,
            });
        };
        this.shouldDisplayEditor = () => {
            const { documentLoadingState } = this.props;
            const { mediaProcessingStatus, projectErrorStatus, } = this.props;
            if (documentLoadingState === 'loaded') {
                // NOTE: If there was a document (even partial) successfully imported, show it,
                // regardless of errors. This is useful for users who edited document before
                // transcription crashed.
                return true;
            }
            if (projectErrorStatus !== 'no-error') {
                const isMicStreaming = projectErrorStatus.reason === 'MissingRecording' && this.isMicSource;
                if (!isMicStreaming) {
                    return false;
                }
            }
            // NOTE: Microphone: keep the editor visible while waiting for the first words
            // (no full-screen loader).
            if (this.isMicSource) {
                return documentLoadingState !== 'failed';
            }
            return (mediaProcessingStatus === 'words-arriving'
                || mediaProcessingStatus === 'completed');
        };
        this.isMicSource = transcriptionSourceSignal.peek() === 'mic';
        this.logger = props.logger;
        this.state = {
            speakerSelectDialog: {
                visible: false,
                node: null,
                replacingEverywhere: false,
                replacedSpeaker: null,
            },
            sectionTagsModal: {
                visible: false,
                tags: [],
                node: null,
            },
            summary: {
                modalVisible: false,
                from: 0,
                to: 0,
                state: 'ready',
            },
            sectionName: {
                modalVisible: false,
                target: null,
            },
            isDocumentComplete: false,
            isEditorVisible: false,
        };
        const { editorController, project, onProjectUpdated, onLeaveEditorRequested, session, messageStream, enqueueProjectUpdate, logger, isSecondary, } = this.props;
        if (!isSecondary) {
            this.documentSaver = new DocumentSaver(project, onProjectUpdated, enqueueProjectUpdate, session, editorController, onLeaveEditorRequested, displayNotification, logger);
            this.recognition = new Recognition(editorController, messageStream, this.handleHistoryLoaded, () => {
                void this.handleTranscriptionStarted();
            });
        }
        else {
            this.documentSaver = null;
            this.recognition = null;
        }
        editorController.onSelectSpeaker = this.showAssignSpeakerModal;
        editorController.onReplaceSpeaker = this.showSpeakerReplaceModal;
        editorController.onGenerateSummary = this.showSummaryModal;
        editorController.onSectionHeadingClicked = this.showSectionNameModal;
        editorController.onDocumentChanged = (shouldRequestSave) => {
            if (shouldRequestSave && this.documentSaver !== null) {
                void this.documentSaver.handleActivity();
            }
        };
        this.editorElementRef = createRef();
    }
    async componentDidUpdate(prevProps, prevState) {
        const { isPlayerReady, tutorialContinue, tutorialGoNext, tutorialCurrentStep, project, onProjectUpdated, editorController, mediaProcessingStatus, onLeaveEditorRequested, session, lock, trsxLabel, isDoubleEditorMode, isSecondary, } = this.props;
        const { isEditorVisible, speakerSelectDialog } = this.state;
        const { documentLoadingState } = this.props;
        const prevDocumentLoadingState = prevProps.documentLoadingState;
        const isTranscriptionRunning = this.isStreamingSessionActive
            || mediaProcessingStatus === 'words-arriving'
            || project.processingState === 'InProgress';
        if (isDoubleEditorMode
            && prevProps.trsxLabel !== trsxLabel
            && (documentLoadingState === 'loaded' || documentLoadingState === 'no-document')) {
            if (!isSecondary && isTranscriptionRunning) {
                this.logger.info('Editor label changed to "{label}" during active transcription. Skipping TRSX reload.', { label: trsxLabel });
            }
            else {
                this.logger.info('Editor label changed to "{label}". Reloading TRSX.', {
                    label: trsxLabel,
                });
                await this.reloadTrsxWithLabel(trsxLabel);
            }
        }
        if (prevProps.lock.isLocked !== lock.isLocked
            && (documentLoadingState === 'loaded' || documentLoadingState === 'no-document')) {
            this.logger.info('Lock state changed. Editor is now {isLocked}.', {
                isLocked: lock.isLocked,
            });
            if (lock.isLocked) {
                editorController.disableEditing();
            }
            else {
                editorController.enableEditing();
            }
        }
        if (this.isStreamingSessionActive
            && mediaProcessingStatus === 'completed'
            && prevProps.mediaProcessingStatus !== 'completed') {
            this.logger.info('Media processing status changed to "completed" (from "{prev}").', {
                prev: prevProps.mediaProcessingStatus,
            });
            await this.handleTranscriptionComplete();
        }
        if (prevProps.project.processingState === 'InProgress'
            && project.processingState !== 'InProgress') {
            await this.checkTranscriptionAccess();
        }
        const isPlayerAndDocumentReady = documentLoadingState !== 'loading' && isPlayerReady;
        const wasPlayerAndDocumentReady = prevDocumentLoadingState !== 'loading' && prevProps.isPlayerReady;
        if (isPlayerAndDocumentReady && !wasPlayerAndDocumentReady) {
            tutorialContinue();
            void this.handlePlayerAndDocumentLoadingFinished();
        }
        const newIsEditorVisible = this.shouldDisplayEditor();
        if (prevState.isEditorVisible !== newIsEditorVisible) {
            this.logger.info('Editor visibility changed. Is visible: {isVisible}.', { isVisible: newIsEditorVisible });
            this.setState({ isEditorVisible: newIsEditorVisible });
        }
        if (isEditorVisible !== prevState.isEditorVisible) {
            // Grammarly popups is rendered after a while
            this.pluginsTimerId = window.setTimeout(this.checkPlugins, 5000);
        }
        if (!deepEqual(prevProps.project.keywordsHighlight, project.keywordsHighlight)) {
            void editorController.keywords.highlightAllKeywords(project.keywordsHighlight);
        }
        // update documentSaver just in case that some props that were passed
        // in the constructor changed.
        if (this.documentSaver !== null) {
            this.documentSaver.update(project, onProjectUpdated, session, editorController, onLeaveEditorRequested);
        }
        if (!prevState.speakerSelectDialog.visible
            && speakerSelectDialog.visible
            && tutorialCurrentStep === 3) {
            tutorialGoNext();
        }
    }
    async componentDidMount() {
        const { editorController, onTranscriptionLoadFailed, isSecondary, notification, lock, } = this.props;
        if (this.editorElementRef.current === null) {
            this.logger.error('editorElementRef is not set, cannot mount Quill.');
            throw new Error('editorElementRef is not set');
        }
        editorController.mount(this.editorElementRef.current, isSecondary ? 'secondary' : 'main');
        try {
            const initializationResult = await this.initializeEditor();
            const initialTrsx = await this.loadTrsx('initialTrsx');
            if (initializationResult !== null) {
                this.logger.info('Editor initialization finished successfully. Document is ready.');
                this.updateDocumentLoadingState('loaded');
                if (lock.isLocked) {
                    editorController.disableEditing();
                }
                const trsxToAlign = initialTrsx.isSuccess()
                    ? initialTrsx.get().content
                    : initializationResult.content;
                editorController.initializeAligner(trsxToAlign);
                this.checkRecentEdits();
            }
            else {
                this.logger.info('Editor initialization finished with no document to display.');
                this.updateDocumentLoadingState('no-document');
                if (lock.isLocked) {
                    editorController.disableEditing();
                }
                return;
            }
            this.setState({ isDocumentComplete: initialTrsx.isSuccess() });
        }
        catch (error) {
            this.logger.error('CRITICAL: Opening or initializing project failed: {error}', { error: String(error) });
            this.updateDocumentLoadingState('failed');
            notification.error({
                message: `Opening or initializing project failed: ${String(error)}`,
                placement: 'topRight',
                duration: 0,
            });
            onTranscriptionLoadFailed();
        }
    }
    componentWillUnmount() {
        var _a;
        this.logger.info('Cleaning up resources (DocumentSaver, Recognition, WebSockets).');
        (_a = this.recognition) === null || _a === void 0 ? void 0 : _a.destroy();
        if (this.connectedMessageStream !== null) {
            void this.connectedMessageStream.close();
        }
        window.clearTimeout(this.pluginsTimerId);
        if (this.documentSaver !== null) {
            this.documentSaver.cleanup();
            setSavingStateSignal('saved');
        }
    }
    render() {
        var _a, _b, _c, _d, _e;
        const { speakerSelectDialog, sectionTagsModal, summary, sectionName, } = this.state;
        const { position, className, editorController, mediaProcessingStatus, isCaptionMode, tutorialGoNext, tutorialCurrentStep, project, session, onScroll, isSecondary, isDoubleEditorMode, lock, trsxLabel = '', editorLabelOptions = [], onEditorLabelChange = undefined, projectErrorStatus, documentLoadingState, documentSpeakersOnly = false, } = this.props;
        const { mediaSource, playerState } = this.context;
        const defaultSpeakerColor = (_b = (_a = editorController.captions.parameters) === null || _a === void 0 ? void 0 : _a.defaultColor) !== null && _b !== void 0 ? _b : subtitlesDefaults.speakerColor;
        const isRightToLeft = (_d = (_c = editorController.getLanguage()) === null || _c === void 0 ? void 0 : _c.isRightToLeft()) !== null && _d !== void 0 ? _d : false;
        const onPressEnter = (e) => {
            if (e.key === 'Enter' && tutorialCurrentStep === 1) {
                tutorialGoNext();
            }
        };
        const handleScroll = () => {
            if (!isDoubleEditorMode) {
                return;
            }
            onScroll(editorController.takeScrollAnchor());
        };
        const showNativeContextMenu = (event) => {
            if (!event.altKey) {
                event.stopPropagation();
            }
        };
        const isVideoUnplayable = () => {
            if (playerState.status !== 'ready') {
                return false;
            }
            if (mediaSource.type === 'local') {
                // for local files we cannot reliably tell if there should be a video or not.
                // Browser guesses it from the file extension, but mp4 could be audio only.
                return false;
            }
            return mediaSource.remoteHasVideo && playerState.video === 'no-video';
        };
        const isMicStreaming = projectErrorStatus !== 'no-error'
            && projectErrorStatus.reason === 'MissingRecording'
            && transcriptionSourceSignal.peek() === 'mic';
        const documentLayout = computeDocumentVerticalLayout(position, 0);
        const isMicPrimaryEditor = this.isMicSource && !isSecondary;
        const showEditorForMic = isMicPrimaryEditor && this.shouldDisplayEditor();
        const documentStillOpening = documentLoadingState === 'loading';
        const micStreamingAwaitingWords = isMicStreaming && mediaProcessingStatus !== 'words-arriving';
        const showMicTranscriptionDots = showEditorForMic
            && (documentStillOpening || micStreamingAwaitingWords);
        const remUnit = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const editorFontSizePx = session.login.user.settings.editorFontDelta + remUnit;
        /* NOTE: Speakers and sections are undefined on mount */
        /* eslint-disable @typescript-eslint/no-unnecessary-condition */
        const showDocumentWidgets = editorController.speakers !== undefined
            && editorController.sections !== undefined;
        /* eslint-enable @typescript-eslint/no-unnecessary-condition */
        return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        _jsx("div", { style: documentLayout.transcription, className: className, onKeyUp: onPressEnter, children: _jsx(ContextMenu, { editorController: editorController, isCaptionMode: isCaptionMode, disabled: !this.shouldDisplayEditor() || project.isReadOnly || lock.isLocked, children: _jsxs("div", { onContextMenu: showNativeContextMenu, style: documentLayout.transcription, children: [isVideoUnplayable()
                            ? (_jsx(ProjectAlert, { type: "warning", title: txt('videoNotAvailable'), description: _jsx("p", { children: txt('convertToH264') }), contactSupport: true, closable: true })) : null, _jsxs(DocumentHoverElement, { disabled: project.isReadOnly || isSecondary || lock.isLocked, editorController: editorController, children: [(isSecondary || lock.isLocked) && (_jsx(Tooltip, { title: txt('readOnly'), children: _jsx(LockOutlined, { className: styles.readOnlyTag }) })), isDoubleEditorMode && (_jsxs(_Fragment, { children: [_jsx("div", { className: clsx(styles.selectBackground, {
                                                [styles.selectBackgroundReadOnly]: project.isReadOnly
                                                    || isSecondary || lock.isLocked,
                                            }) }), _jsx(Select, { value: trsxLabel, onChange: onEditorLabelChange, className: styles.editorLabelSelect, size: "small", options: editorLabelOptions, disabled: mediaProcessingStatus !== 'completed', popupMatchSelectWidth: false })] })), _jsxs("div", { className: styles.quillContainerWrapper, style: Object.assign(Object.assign({}, documentLayout.transcription), { display: this.shouldDisplayEditor() ? 'block' : 'none' }), children: [showMicTranscriptionDots ? (_jsx("div", { className: styles.micTranscriptionDotsOverlay, children: _jsx(MicTranscriptionDots, {}) })) : null, _jsx("div", { ref: this.editorElementRef, className: clsx('beey-tutorial-step-speaker sub-mode-tutorial-step-delete beey-tutorial-step-edit', styles.quillContainer, {
                                                [styles.readOnly]: project.isReadOnly || isSecondary || lock.isLocked,
                                                [styles.visibleNbspace]: session.login.user.settings.nonBreakingSpace,
                                                [styles.doubleEditor]: isDoubleEditorMode,
                                            }), style: { fontSize: `${editorFontSizePx}px` }, role: "textbox", tabIndex: 0, "aria-label": "Text Editor", onScroll: handleScroll, children: showDocumentWidgets
                                                ? (_jsx(DocumentWidgets, { editorRef: this.editorElementRef, editorController: editorController, isCaptionMode: isCaptionMode, isRightToLeft: isRightToLeft, onClick: this.handleWidgetsClicked, disabled: project.isReadOnly || isSecondary || lock.isLocked })) : null })] })] }), _jsx(SummaryModal, { onClose: this.closeSummaryModal, visible: summary.modalVisible, onSubmit: this.generateSummary, status: summary.state, errorMessage: summary.errorMessage }), _jsx(SectionNameModal, { visible: sectionName.modalVisible, target: sectionName.target, onSectionRemoved: (target) => editorController.sections.removeSection(target), onSubmit: (sectionValues, target) => {
                                editorController.sections.handleSectionNameSelected(sectionValues, target);
                                this.closeSectionNameModal();
                            }, onClose: this.closeSectionNameModal, project: project }), _jsx("div", { children: speakerSelectDialog.visible && (_jsx(SpeakerEditorModal, { onSubmit: this.handleSpeakerSelected, editorController: editorController, onClose: this.closeSpeakerSelectDialog, language: editorController.getLanguage(), isCaptionMode: isCaptionMode, defaultSpeakerColor: defaultSpeakerColor, replacedSpeaker: (_e = speakerSelectDialog.replacedSpeaker) !== null && _e !== void 0 ? _e : undefined, replaceEverywhere: speakerSelectDialog.replacingEverywhere, speakerNode: speakerSelectDialog.node, documentSpeakersOnly: documentSpeakersOnly })) }), _jsx("div", { children: sectionTagsModal.visible ? (_jsx(SectionTagsModal, { onCancel: this.closeSectionTagsModal, onSelectTags: this.handleSectionTagsSelected, targetNode: sectionTagsModal.node })) : null })] }) }) }));
    }
    importTrsxToEditorController(trsx, label, sourceVariant = 'currentTrsx') {
        const { editorController, project, isSecondary } = this.props;
        if (!isSecondary && this.isStreamingSessionActive && sourceVariant === 'currentTrsx') {
            this.logger.info('Skipped importing currentTrsx to primary editor during active transcription.', {
                label,
                importedEtag: trsx.etag,
            });
            return;
        }
        const language = getLanguageFromTrsx(trsx.content);
        editorController.importTrsx(Object.assign(Object.assign({}, trsx), { label, language }), false, project, undefined);
    }
    handlePlayerAndDocumentLoadingFinished() {
        const { editorController, project, isSecondary, lock, } = this.props;
        const { isDocumentComplete } = this.state;
        const urlParams = new URLSearchParams(window.location.search);
        const timestampInSeconds = urlParams.get('timestamp');
        const search = urlParams.get('search');
        const speakerName = urlParams.get('speakername');
        if (timestampInSeconds !== null && search !== null) {
            editorController.highlightSearchResult(timestampInSeconds, search);
        }
        else if (speakerName !== null) {
            editorController.highlightSpeaker(speakerName);
        }
        else {
            // focuses the editor where the user left it.
            editorController.syncTextHighlightWithPlayback(true);
        }
        editorController.handlePlayerAndDocumentLoadingFinished(isDocumentComplete);
        if (project.isReadOnly === true || isSecondary || lock.isLocked) {
            editorController.disableEditing();
        }
    }
    updateDocumentLoadingState(next) {
        const { onDocumentLoadingStateChange } = this.props;
        onDocumentLoadingStateChange(next);
    }
}
DocumentEditor.contextType = playbackContext;
